import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import {
  auth,
  FormikTextInput,
  getMultiFactorResolver,
  logger,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  useAlert,
  useTheme,
} from "capsule"
import { InputScreen } from "capsule/components/Screens/InputScreen"
import { FormikConfig } from "formik"
import React, { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform, Pressable, StyleSheet, View } from "react-native"
import { Text } from "react-native-paper"
import * as Yup from "yup"

import { INPUT_HEIGHT } from "../../features/config/Constants"
import { LoginNS, PasswordModificationNS } from "../../features/i18n/constants"
import { RootParamList } from "../../features/Navigation/types"
import { useMfaResolver, useNewPassword } from "../../features/Providers/MfaProvider"
import { PasswordModificationFormValues } from "./Constants"

const PasswordModificationScreen: FC = () => {
  const {
    dimensions: { spacing },
    colors: { surface, primary },
  } = useTheme()
  const { showSnack } = useAlert()
  const { setResolver } = useMfaResolver()
  const { setNewPassword } = useNewPassword()
  const navigation =
    useNavigation<StackNavigationProp<RootParamList, "ValidationMfaPasswordModification">>()
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation(PasswordModificationNS)
  const { t: tRegister } = useTranslation(LoginNS)
  const s = useMemo(
    () => ({
      // view and container style
      content: [
        styles.content,
        {
          backgroundColor: surface.background,
          paddingVertical: spacing,
        },
      ],
      // children styles
      child: { paddingBottom: spacing },
      input: [styles.input],
      link: [
        styles.link,
        {
          color: primary,
        },
      ],
    }),

    [primary, spacing, surface.background],
  )

  const handleSubmit = async ({ oldPassword, newPassword }) => {
    setLoading(true)
    const user = auth().currentUser
    try {
      if (!user?.email) {
        showSnack({ message: t("errors.noUser") })
        return
      }
      const credential = auth.EmailAuthProvider.credential(user.email, oldPassword)
      await user.reauthenticateWithCredential(credential)
    } catch (e) {
      switch (e.code) {
        case "auth/wrong-password":
          showSnack({ message: t("errors.invalidOldPassword") })
          break
        case "auth/multi-factor-auth-required":
          let recaptchaVerifier
          if (Platform.OS === "web") {
            // @ts-ignore
            recaptchaVerifier = auth().RecaptchaVerifier("recaptcha-container", {
              size: "invisible",
            })
          }
          const resolverTemp = getMultiFactorResolver(auth(), e)

          if (resolverTemp.hints.length === 0) {
            logger("Resolver hints is empty")
            showSnack({ message: t("errors.unknown") })
            return
          }
          if (resolverTemp.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
            const hint = resolverTemp.hints[0]
            const phoneAuthProvider = new PhoneAuthProvider(auth())
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(
              {
                multiFactorHint: hint,
                session: resolverTemp.session,
              },
              Platform.OS === "web" ? recaptchaVerifier : undefined,
            ) // triggers the message to the user

            setResolver?.(resolverTemp)
            setNewPassword?.(newPassword)
            navigation.navigate("ValidationMfaPasswordModification", {
              verifId: verificationId,
            })
          }
          break
        default:
          showSnack({ message: t("errors.unknown") })
      }
    } finally {
      setLoading(false)
    }
  }

  const formikConfig: FormikConfig<PasswordModificationFormValues> = {
    onSubmit: handleSubmit,
    validateOnMount: true,
    initialValues: {
      oldPassword: "",
      newPassword: "",
      newPasswordVerification: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required(t("errors.oldPasswordNeeded")),
      newPassword: Yup.string()
        .notOneOf([Yup.ref("oldPassword"), null], t("errors.passwordsMustNotMatch"))
        .min(8, tRegister("errors.minLength"))
        .matches(/[A-Z]/, tRegister("errors.upperCaseNeeded"))
        .matches(/[0-9]/, tRegister("errors.numberNeeded"))
        .matches(/[^A-Za-z0-9]/, tRegister("errors.specialCharNeeded"))
        .required(t("errors.newPasswordNeeded")),
      newPasswordVerification: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], t("errors.passwordsMustMatch"))
        .required(t("errors.passwordVerificationNeeded")),
    }),
  }
  const handleForgetPassword = async () => {
    navigation.navigate("ForgotPassword")
  }
  return (
    <View style={s.content}>
      <InputScreen<PasswordModificationFormValues>
        formLoading={!loading}
        Inputs={
          <View>
            <FormikTextInput
              style={s.input}
              name="oldPassword"
              autoComplete="off"
              autoCorrect={false}
              textContentType="none"
              spellCheck={false}
              secureTextEntry
              styles={{ child: s.child }}
              label={t("labels.oldPassword")}
              testID="old_password"
            />
            <FormikTextInput
              style={s.input}
              name="newPassword"
              autoComplete="off"
              autoCorrect={false}
              textContentType="none"
              spellCheck={false}
              secureTextEntry
              styles={{ child: s.child }}
              label={t("labels.newPassword")}
              testID="new_password"
            />
            <View style={s.child}>
              <Text>{tRegister("passwordCaption")} :</Text>
              <Text>- {tRegister("minLength")}</Text>
              <Text>- {tRegister("needUppercase")}</Text>
              <Text>- {tRegister("needNumber")}</Text>
              <Text>- {tRegister("needSpecialChar")}</Text>
            </View>
            <FormikTextInput
              style={s.input}
              name="newPasswordVerification"
              autoComplete="off"
              autoCorrect={false}
              textContentType="none"
              secureTextEntry
              spellCheck={false}
              styles={{ child: s.child }}
              label={t("labels.confirmNewPassword")}
              testID="new_password_verification"
            />
          </View>
        }
        buttonLabel={t("labels.submit")}
        formikConfig={formikConfig}
      />

      <Pressable onPress={handleForgetPassword}>
        <Text style={styles.link}>{tRegister("forgotPassword")}</Text>
      </Pressable>
    </View>
  )
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  input: {
    height: INPUT_HEIGHT,
    minHeight: INPUT_HEIGHT,
    width: "100%",
  },
  link: {
    width: "100%",
    textAlign: "center",
  },
})

export default PasswordModificationScreen
